export interface Point {
  x: number;
  y: number;
}

export interface PlaneConfig {
  width: number;
  height: number;
  numPoints: number;
  minDistance: number;
  maxAttempts?: number;
}

// Angle in radians where particles will not be placed, centered on Y axis above and below the X axis
const DEFLECTION_ANGLE = 0.85;

const distance = (p1: Point, p2: Point): number => Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));

const isValidPoint = (points: Point[], minDistance: number, candidate: Point): boolean =>
  points.every((existingPoint) => distance(candidate, existingPoint) >= minDistance);

export function generateDistributedPoints(config: PlaneConfig): Point[] {
  const { width, height, numPoints, minDistance, maxAttempts = 1000 } = config;
  const points: Point[] = [];

  const maxR = Math.max(width / 2, height / 2);
  const minR = maxR;

  // Generate points
  for (let i = 0; i < numPoints; i++) {
    let pointAttempts = 0;
    let validPoint: Point | null = null;

    while (pointAttempts < maxAttempts && !validPoint) {
      const r = minR + Math.random() * maxR;

      let theta = 0;
      let thetaAttempts = 0;

      while (thetaAttempts < maxAttempts) {
        // Ensure even distribution on left and right
        theta = Math.random() * (i % 2 == 0 ? 2 : 1) * Math.PI;

        if (
          !(
            (theta > Math.PI / 2 - DEFLECTION_ANGLE && theta < Math.PI / 2 + DEFLECTION_ANGLE) ||
            (theta > Math.PI * 1.5 - DEFLECTION_ANGLE && theta < Math.PI * 1.5 + DEFLECTION_ANGLE)
          )
        ) {
          break;
        }

        thetaAttempts++;
      }

      const candidate: Point = {
        x: r * Math.cos(theta) + width / 2,
        y: r * Math.sin(theta) + height / 2,
      };

      if (isValidPoint(points, minDistance, candidate)) {
        validPoint = candidate;
      }
      pointAttempts++;
    }

    if (validPoint) {
      points.push(validPoint);
    }
  }

  return points;
}
